import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  InputProps,
  Pagination,
  RadioGroup,
  SpaceBetween,
  Table,
  TextFilter,
  TextFilterProps,
} from "@amzn/awsui-components-react";
import {
  OmsPresetMetadata,
  OmsPresetScope,
} from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { reportTimestamp } from "./OMSReportSchedule";
import { presetScopeLabel } from "./omsPresetEditing";

export function OMSPresetSave({
  name,
  scope,
  onName,
  onScope,
  filterCount,
  columnSummary,
  validFilters,
  validColumns,
  attempted,
  nameRef,
  disabled,
}: {
  name: string;
  scope: OmsPresetScope;
  onName: (name: string) => void;
  onScope: (scope: OmsPresetScope) => void;
  filterCount: number;
  columnSummary: string;
  validFilters: boolean;
  validColumns: boolean;
  attempted: boolean;
  nameRef: React.RefObject<InputProps.Ref>;
  disabled: boolean;
}) {
  return (
    <SpaceBetween size="l">
      <Box color="text-body-secondary">
        Save settings for future reports. Presets are private to you and this
        product.
      </Box>
      <div className="oms-preset-name">
        <FormField
          label="Preset name"
          constraintText="Up to 80 characters. Names must be unique for this product."
          errorText={
            attempted && !name.trim() ? "Enter a preset name." : undefined
          }
        >
          <Input
            ref={nameRef}
            autoFocus
            disabled={disabled}
            ariaLabel="Preset name"
            value={name}
            onChange={({ detail }) => onName(detail.value)}
          />
        </FormField>
      </div>
      <FormField label="Save">
        <RadioGroup
          value={scope}
          items={[
            { value: "both", label: "Filters and columns", disabled },
            { value: "filters", label: "Filters only", disabled },
            { value: "columns", label: "Columns only", disabled },
          ]}
          onChange={({ detail }) => onScope(detail.value as OmsPresetScope)}
        />
      </FormField>
      <SpaceBetween size="s">
        {scope !== "columns" &&
          (validFilters ? (
            <Box>
              {filterCount} active {filterCount === 1 ? "filter" : "filters"}
            </Box>
          ) : (
            <Alert type="warning">
              Choose Filtered rows and correct the active filters before saving
              them.
            </Alert>
          ))}
        {scope !== "filters" &&
          (validColumns ? (
            <Box>{columnSummary}</Box>
          ) : (
            <Alert type="warning">
              Correct the column selection before saving it.
            </Alert>
          ))}
      </SpaceBetween>
    </SpaceBetween>
  );
}

type PresetManagement = {
  state: OmsPresetsState;
  rename?: { id: string; name: string };
  setRename: React.Dispatch<
    React.SetStateAction<{ id: string; name: string } | undefined>
  >;
  deleting?: string;
  setDeleting: React.Dispatch<React.SetStateAction<string | undefined>>;
  input: React.RefObject<InputProps.Ref>;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
};

function presetNameCell(preset: OmsPresetMetadata, controls: PresetManagement) {
  const { rename, state, input, setRename } = controls;
  return rename?.id === preset.presetId ? (
    <FormField label="New name">
      <Input
        ref={input}
        autoFocus
        ariaLabel={`Rename ${preset.name}`}
        value={rename.name}
        disabled={state.change.isLoading}
        onChange={({ detail }) => setRename({ ...rename, name: detail.value })}
      />
    </FormField>
  ) : (
    <Box fontWeight="bold">{preset.name}</Box>
  );
}

function presetActionsCell(
  preset: OmsPresetMetadata,
  controls: PresetManagement
) {
  const {
    rename,
    state,
    input,
    setRename,
    deleting,
    setDeleting,
    onDeleted,
    onRenamed,
  } = controls;
  if (rename?.id === preset.presetId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          disabled={state.change.isLoading}
          onClick={() => setRename(undefined)}
        >
          Cancel
        </Button>
        <Button
          disabled={!rename.name.trim()}
          loading={state.change.isLoading}
          loadingText="Renaming"
          onClick={() => {
            state.change
              .mutateAsync({
                action: "rename",
                existing: preset,
                name: rename.name,
              })
              .then((result) => {
                if (result.preset) onRenamed(result.preset);
                setRename(undefined);
              })
              .catch(() => input.current?.focus());
          }}
        >
          Save name
        </Button>
      </SpaceBetween>
    );
  if (deleting === preset.presetId)
    return (
      <SpaceBetween size="xs">
        <Box>Delete “{preset.name}”?</Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            disabled={state.change.isLoading}
            onClick={() => setDeleting(undefined)}
          >
            Cancel
          </Button>
          <Button
            loading={state.change.isLoading}
            loadingText="Deleting"
            onClick={() => {
              state.change
                .mutateAsync({ action: "delete", existing: preset })
                .then(() => {
                  onDeleted(preset.presetId);
                  setDeleting(undefined);
                })
                .catch(() => undefined);
            }}
          >
            Delete preset
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        disabled={state.change.isLoading}
        onClick={() => {
          state.change.reset();
          setDeleting(undefined);
          setRename({ id: preset.presetId, name: preset.name });
        }}
      >
        Rename
      </Button>
      <Button
        disabled={state.change.isLoading}
        onClick={() => {
          state.change.reset();
          setRename(undefined);
          setDeleting(preset.presetId);
        }}
      >
        Delete
      </Button>
    </SpaceBetween>
  );
}

export function OMSPresetManager({
  state,
  onDeleted,
  onRenamed,
}: {
  state: OmsPresetsState;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rename, setRename] = useState<{ id: string; name: string }>();
  const [deleting, setDeleting] = useState<string>();
  const input = useRef<InputProps.Ref>(null);
  const searchControl = useRef<TextFilterProps.Ref>(null);
  useEffect(() => searchControl.current?.focus(), []);
  const controls = {
    state,
    rename,
    setRename,
    deleting,
    setDeleting,
    input,
    onDeleted: (id: string) => {
      onDeleted(id);
      searchControl.current?.focus();
    },
    onRenamed: (preset: OmsPresetMetadata) => {
      onRenamed(preset);
      searchControl.current?.focus();
    },
  };
  const items = (state.list.data || []).filter((preset) =>
    preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  );
  const pages = Math.max(1, Math.ceil(items.length / 10));
  const currentPage = Math.min(page, pages);
  return (
    <SpaceBetween size="m">
      <Box color="text-body-secondary">
        Manage your presets for this product. Existing exports and applied
        settings are preserved.
      </Box>
      {(state.list.isError || state.change.isError) && (
        <Alert type="error" header="Preset change unavailable">
          {presetErrorMessage(state.change.error || state.list.error)}
          <Button
            variant="inline-link"
            onClick={() => {
              state.list.refetch().catch(() => undefined);
            }}
          >
            Reload list
          </Button>
        </Alert>
      )}
      <Table<OmsPresetMetadata>
        variant="embedded"
        loading={state.list.isLoading}
        loadingText="Loading your presets"
        items={items.slice((currentPage - 1) * 10, currentPage * 10)}
        trackBy="presetId"
        filter={
          <TextFilter
            ref={searchControl}
            filteringText={search}
            filteringPlaceholder="Search preset names"
            filteringAriaLabel="Search preset names"
            filteringClearAriaLabel="Clear preset search"
            onChange={({ detail }) => {
              setSearch(detail.filteringText);
              setPage(1);
            }}
          />
        }
        pagination={
          items.length > 10 ? (
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={pages}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
              ariaLabels={{
                nextPageLabel: "Next preset page",
                previousPageLabel: "Previous preset page",
                pageLabel: (index) => `Preset page ${index}`,
              }}
            />
          ) : undefined
        }
        empty={
          <Box textAlign="center" padding="l">
            {search
              ? "No matching presets."
              : "No saved presets for this product."}
          </Box>
        }
        columnDefinitions={[
          {
            id: "name",
            header: "Name",
            width: 300,
            cell: (preset) => presetNameCell(preset, controls),
          },
          {
            id: "scope",
            header: "Settings",
            cell: (preset) => presetScopeLabel[preset.scope],
          },
          {
            id: "updated",
            header: "Last edited",
            cell: (preset) => reportTimestamp(preset.updatedAt),
          },
          {
            id: "actions",
            header: "Actions",
            minWidth: 210,
            cell: (preset) => presetActionsCell(preset, controls),
          },
        ]}
      />
    </SpaceBetween>
  );
}
